import {useEffect, useState, type ReactNode} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Textarea} from '@/components/ui/textarea'
import {Hint} from '@/components/ui/hint'
import {InfoTip} from '@/components/ui/info-tip'
import {CatalogOtherField} from '@/components/catalog-other-field'
import {DateField} from '@/components/date-field'
import {EntrevistaRiesgosStep} from '@/components/entrevista-riesgos-step'
import {FichaFacts} from '@/components/ficha-facts'
import {OnboardingLecturaFallida} from '@/components/onboarding-lectura-fallida'
import {PageHeading} from '@/components/page-heading'
import {SectionCard} from '@/components/section-card'
import {useBusiness} from '@/hooks/empresa/organizacion/use-businesses'
import {useMyFicha, useUpdateMyFicha, type Ficha, type FichaUpdate} from '@/hooks/empresa/identidad/use-ficha'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useMyOnboarding} from '@/hooks/empresa/identidad/use-onboarding'
import {useMyEntrevista} from '@/hooks/sgsst/riesgos/use-entrevista'
import {usePositionDescripcion, useTagCatalog, type TagSearchKind} from '@/hooks/empresa/organizacion/use-tags'
import {MiPerfilSociodemograficoBody} from '@/pages/empresa/mi_espacio/mi-perfil-sociodemografico'
import {
  AFP_OPTIONS,
  ARL_OPTIONS,
  CONTRACT_TYPES,
  EPS_OPTIONS,
  GENERO,
  JORNADA_TYPES,
  NIVEL_EDUCATIVO,
} from '@/lib/ficha-catalogs'
import {translateApiError} from '@/lib/errors'
import {localToday, maxAdultBirthDate} from '@/lib/risk'
import {cn} from '@/lib/utils'

const RECORTE_MAX = 60
function recorte(texto: string): string {
  if (texto.length <= RECORTE_MAX) return texto
  const corte = texto.slice(0, RECORTE_MAX)
  const espacio = corte.lastIndexOf(' ')
  return `${(espacio > 20 ? corte.slice(0, espacio) : corte).trimEnd()}…`
}

// FORM_TOTAL_PAGES del backend; riesgos y perfil son pasos virtuales que nunca viajan como `page_reached`.
const TOTAL_PAGES = 4
const RIESGOS_STEP = TOTAL_PAGES + 1
const PERFIL_STEP = RIESGOS_STEP + 1
// La barra cuenta todos los pasos del asistente; el número impreso sigue siendo la página de la ficha.
const WIZARD_STEPS = PERFIL_STEP

const PAGE_KEY: Record<number, string> = {
  2: 'ficha.pasos.trabajo',
  3: 'ficha.pasos.personales',
  4: 'ficha.pasos.seguridadSocial',
  [RIESGOS_STEP]: 'ficha.pasos.riesgos',
}

type FichaDraft = {
  cargo: string
  cargo_descripcion: string
  area: string
  sede: string
  fecha_ingreso: string
  tipo_vinculacion: string
  jornada_turno: string
  nivel_educativo: string
  genero: string
  fecha_nacimiento: string
  eps: string
  afp: string
  arl: string
}

function draftFromFicha(ficha: Ficha): FichaDraft {
  return {
    cargo: ficha.cargo ?? '',
    cargo_descripcion: ficha.cargo_descripcion ?? '',
    area: ficha.area ?? '',
    sede: ficha.sede ?? '',
    fecha_ingreso: ficha.fecha_ingreso ?? '',
    tipo_vinculacion: ficha.tipo_vinculacion ?? '',
    jornada_turno: ficha.jornada_turno ?? '',
    nivel_educativo: ficha.nivel_educativo ?? '',
    genero: ficha.genero ?? '',
    fecha_nacimiento: ficha.fecha_nacimiento ?? '',
    eps: ficha.eps ?? '',
    afp: ficha.afp ?? '',
    arl: ficha.arl ?? '',
  }
}

function FieldGroup({children}: {children: ReactNode}) {
  return <div className="space-y-4">{children}</div>
}

function TagCatalogField({
  id,
  businessId,
  kind,
  label,
  value,
  onChange,
  onCommit,
  hint,
  disabled,
}: {
  id: string
  businessId: string
  kind: TagSearchKind
  label: string
  value: string
  onChange: (value: string) => void
  onCommit: (value: string) => void
  hint: string
  disabled?: boolean
}) {
  const {data: catalog, isLoading} = useTagCatalog(businessId, kind)
  if (isLoading) return <Skeleton className="h-16 w-full" />
  const options = (catalog ?? []).map((tag) => tag.name)
  return (
    <CatalogOtherField
      id={id}
      label={label}
      options={options}
      value={value}
      onChange={onChange}
      onCommit={onCommit}
      hint={hint}
      disabled={disabled}
    />
  )
}

/** Con un cargo existente muestra la descripción más reciente de quienes ya lo ocupan. */
function CargoField({
  businessId,
  cargo,
  descripcion,
  onCargoChange,
  onCargoCommit,
  onDescripcionChange,
  onDescripcionCommit,
  disabled,
}: {
  businessId: string
  cargo: string
  descripcion: string
  onCargoChange: (value: string) => void
  onCargoCommit: (value: string) => void
  onDescripcionChange: (value: string) => void
  onDescripcionCommit: (value: string) => void
  disabled?: boolean
}) {
  const {t} = useTranslation()
  const {data: catalog, isLoading} = useTagCatalog(businessId, 'position')
  const existing = (catalog ?? []).find((tag) => tag.name === cargo)
  const {data: context} = usePositionDescripcion(businessId, existing?.id)

  if (isLoading) return <Skeleton className="h-16 w-full" />
  const options = (catalog ?? []).map((tag) => tag.name)

  return (
    <div className="space-y-2">
      <CatalogOtherField
        id="ficha-cargo"
        label={t('ficha.cargo')}
        options={options}
        value={cargo}
        onChange={onCargoChange}
        onCommit={onCargoCommit}
        hint={t('ficha.cargoOtroHint')}
        disabled={disabled}
      />
      {cargo && (
        <div className="space-y-2">
          {context?.descripcion && (
            <p className="text-xs text-muted-foreground">
              {t('ficha.cargoDescripcionContext')}{' '}
              <InfoTip
                label={t('ficha.cargoDescripcionContext')}
                tip={context.descripcion}
                testId="ficha-cargo-descripcion-context">
                <span className="underline decoration-dotted">{recorte(context.descripcion)}</span>
              </InfoTip>
            </p>
          )}
          <Label htmlFor="ficha-cargo-descripcion">{t('ficha.cargoDescripcion')}</Label>
          <Textarea
            id="ficha-cargo-descripcion"
            minRows={3}
            maxLength={2000}
            value={descripcion}
            placeholder={t('ficha.cargoDescripcionPlaceholder')}
            disabled={disabled}
            onChange={(e) => onDescripcionChange(e.target.value)}
            onBlur={(e) => onDescripcionCommit(e.target.value)}
          />
        </div>
      )}
    </div>
  )
}

// Cada campo se guarda solo y `page_reached` solo avanza en el servidor: volver atrás no pierde progreso.
type FichaView = 'wizard' | 'resumen' | 'edit'

export function MiFichaBody({businessId, conPerfil = false}: {businessId: string; conPerfil?: boolean}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const {data: ficha, isLoading, isError} = useMyFicha(businessId)
  const {data: onboarding, isError: onboardingError, refetch: refetchOnboarding} = useMyOnboarding(businessId)
  const update = useUpdateMyFicha(businessId)
  // Comparte la clave de `EntrevistaRiesgosStep`, así que no agrega un viaje.
  const [page, setPage] = useState(2)
  const {data: entrevista} = useMyEntrevista(page === RIESGOS_STEP ? businessId : undefined)

  const [draft, setDraft] = useState<FichaDraft | null>(null)
  const [view, setView] = useState<FichaView>('wizard')
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (!ficha || draft || !onboarding) return
    setDraft(draftFromFicha(ficha))
    const reached = ficha.form_page_reached
    if (reached < TOTAL_PAGES) {
      setPage(Math.max(reached, 2))
      return
    }
    // Retoma donde quedó el registro; solo con todo resuelto abre el resumen (KAI-219).
    const riesgosStepPendiente =
      onboarding.pendiente?.cta === 'completar_entrevista' || onboarding.pendiente_reconfirmacion
    const perfilPendiente = onboarding.pendiente?.cta === 'completar_perfil'
    if (riesgosStepPendiente) setPage(RIESGOS_STEP)
    else if (conPerfil && perfilPendiente) setPage(PERFIL_STEP)
    else setView('resumen')
  }, [ficha, onboarding, draft, conPerfil])

  // La entrevista desemboca en el perfil sin botón intermedio (KAI-219).
  useEffect(() => {
    if (!conPerfil || view !== 'wizard' || page !== RIESGOS_STEP) return
    if (!entrevista?.completed_at) return
    if (!onboarding || onboarding.perfil_sellado_vigente) return
    setPage(PERFIL_STEP)
  }, [conPerfil, view, page, entrevista, onboarding])

  if (isLoading) return <Skeleton className="h-72 w-full" />
  // Un registro rechazado responde 403 para siempre; sin esto el esqueleto no terminaría.
  if (isError)
    return (
      <div className="surface p-5" data-testid="mi-ficha-rejected">
        <p className="font-heading text-lg text-foreground">{t('ficha.rejectedTitle')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t('ficha.rejectedBody')}</p>
      </div>
    )
  // Sin saber qué le falta no se abre el resumen: un fallo no es «todo completo».
  if (onboardingError && !onboarding) return <OnboardingLecturaFallida onRetry={() => void refetchOnboarding()} />
  if (!ficha || !draft) return <Skeleton className="h-72 w-full" />

  function updateField(field: keyof FichaDraft, value: string) {
    setDraft((d) => (d ? {...d, [field]: value} : d))
  }

  function commit(fields: Partial<FichaUpdate>, pageReached = Math.min(page, TOTAL_PAGES)) {
    if (godReadOnly) return
    setSaveError(null)
    update.mutate({page_reached: pageReached, ...fields}, {onError: (err) => setSaveError(translateApiError(err))})
  }

  function goToPage(next: number) {
    setPage(next)
    // Sube `page_reached` aunque cada campo ya se haya guardado solo.
    commit({}, next)
  }

  const reached = ficha.form_page_reached
  const completed = reached >= TOTAL_PAGES
  const editing = view === 'edit'
  const showPage = (p: number) => editing || (view === 'wizard' && page === p)

  // Fuera del `SectionCard`: el cuerpo del perfil trae sus propias tarjetas.
  if (view === 'wizard' && page === PERFIL_STEP) {
    return (
      <div className="space-y-4" data-testid="mi-ficha">
        <MiPerfilSociodemograficoBody businessId={businessId} />
        {onboarding?.perfil_sellado_vigente && (
          <div className="flex justify-end">
            <Button asChild size="sm" data-testid="mi-ficha-perfil-listo">
              <Link to={`/businesses/${businessId}`}>{t('ficha.perfil.irAMiEspacio')}</Link>
            </Button>
          </div>
        )}
      </div>
    )
  }

  const cardTitle = editing
    ? t('ficha.editar.titulo')
    : view === 'resumen'
      ? t('ficha.resumen.titulo')
      : t(PAGE_KEY[page] ?? 'ficha.pasos.trabajo')
  return (
    <div data-testid="mi-ficha">
      <SectionCard
        title={cardTitle}
        actions={
          view === 'wizard' &&
          page <= TOTAL_PAGES && (
            <div className="flex items-center gap-3">
              <p
                className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase"
                data-testid="ficha-page-indicator">
                {t('ficha.pageIndicator', {page, total: TOTAL_PAGES})}
              </p>
              <div className="flex gap-1" aria-hidden="true">
                {Array.from({length: WIZARD_STEPS}, (_, i) => i + 1).map((p) => (
                  <span
                    key={p}
                    className={cn('h-1.5 w-4 rounded-full', p <= reached ? 'bg-primary' : 'bg-secondary')}
                  />
                ))}
              </div>
            </div>
          )
        }>
        {view === 'resumen' && (
          <div className="space-y-4" data-testid="mi-ficha-resumen">
            <FichaFacts ficha={ficha} />
            <div className="flex flex-wrap items-center gap-3">
              {!godReadOnly && (
                <Button type="button" size="sm" onClick={() => setView('edit')} data-testid="mi-ficha-editar">
                  {t('ficha.resumen.editar')}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setView('wizard')
                  setPage(RIESGOS_STEP)
                }}
                data-testid="mi-ficha-ver-riesgos">
                {t('ficha.resumen.verRiesgos')}
              </Button>
            </div>
          </div>
        )}

        {saveError && (
          <p className="mb-3 text-sm text-destructive" data-testid="mi-ficha-save-error">
            {saveError}
          </p>
        )}

        <div className="space-y-4">
          {view === 'wizard' && page === RIESGOS_STEP && (
            <>
              {completed && (
                <button
                  type="button"
                  onClick={() => setView('resumen')}
                  className="text-xs font-medium text-primary hover:underline"
                  data-testid="mi-ficha-volver-resumen">
                  {t('ficha.resumen.volver')}
                </button>
              )}
              <EntrevistaRiesgosStep businessId={businessId!} />
            </>
          )}

          {showPage(2) && (
            <FieldGroup>
              <CargoField
                businessId={businessId!}
                cargo={draft.cargo}
                descripcion={draft.cargo_descripcion}
                onCargoChange={(v) => updateField('cargo', v)}
                onCargoCommit={(v) => commit({cargo: v || null})}
                onDescripcionChange={(v) => updateField('cargo_descripcion', v)}
                onDescripcionCommit={(v) => commit({cargo_descripcion: v || null})}
                disabled={godReadOnly}
              />
              <TagCatalogField
                id="ficha-area"
                businessId={businessId!}
                kind="area"
                label={t('ficha.area')}
                value={draft.area}
                onChange={(v) => updateField('area', v)}
                onCommit={(v) => commit({area: v || null})}
                hint={t('ficha.areaOtroHint')}
                disabled={godReadOnly}
              />
              <TagCatalogField
                id="ficha-sede"
                businessId={businessId!}
                kind="sede"
                label={t('ficha.sede')}
                value={draft.sede}
                onChange={(v) => updateField('sede', v)}
                onCommit={(v) => commit({sede: v || null})}
                hint={t('ficha.sedeOtroHint')}
                disabled={godReadOnly}
              />
              <DateField
                id="ficha-fecha-ingreso"
                label={t('ficha.fechaIngreso')}
                value={draft.fecha_ingreso}
                max={localToday()}
                hint={t('ficha.fechaIngresoHelp')}
                disabled={godReadOnly}
                onChange={(v) => {
                  updateField('fecha_ingreso', v)
                  commit({fecha_ingreso: v || null})
                }}
              />
              <div className="space-y-2">
                <Label htmlFor="ficha-tipo-vinculacion">{t('ficha.tipoVinculacion')}</Label>
                <select
                  id="ficha-tipo-vinculacion"
                  value={draft.tipo_vinculacion}
                  onChange={(e) => {
                    updateField('tipo_vinculacion', e.target.value)
                    commit({tipo_vinculacion: e.target.value || null})
                  }}
                  disabled={godReadOnly}
                  className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
                  <option value="">{t('employees.contractType.unspecified')}</option>
                  {CONTRACT_TYPES.map((c) => (
                    <option key={c} value={c}>
                      {t(`employees.contractType.${c}`)}
                    </option>
                  ))}
                </select>
                <Hint testId="ficha-tipo-vinculacion-hint">{t('ficha.tipoVinculacionHelp')}</Hint>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ficha-jornada-turno">{t('ficha.jornadaTurno')}</Label>
                <select
                  id="ficha-jornada-turno"
                  value={draft.jornada_turno}
                  onChange={(e) => {
                    updateField('jornada_turno', e.target.value)
                    commit({jornada_turno: e.target.value || null})
                  }}
                  disabled={godReadOnly}
                  className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
                  <option value="">{t('ficha.jornadaOptions.unspecified')}</option>
                  {JORNADA_TYPES.map((j) => (
                    <option key={j} value={j}>
                      {t(`ficha.jornadaOptions.${j}`)}
                    </option>
                  ))}
                </select>
              </div>
            </FieldGroup>
          )}

          {showPage(3) && (
            <FieldGroup>
              <div className="space-y-2">
                <Label htmlFor="ficha-nivel-educativo">{t('ficha.nivelEducativo')}</Label>
                <select
                  id="ficha-nivel-educativo"
                  value={draft.nivel_educativo}
                  onChange={(e) => {
                    updateField('nivel_educativo', e.target.value)
                    commit({nivel_educativo: e.target.value || null})
                  }}
                  disabled={godReadOnly}
                  className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
                  <option value="">{t('ficha.nivelEducativoOptions.unspecified')}</option>
                  {NIVEL_EDUCATIVO.map((n) => (
                    <option key={n} value={n}>
                      {t(`ficha.nivelEducativoOptions.${n}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ficha-genero">{t('ficha.genero')}</Label>
                <select
                  id="ficha-genero"
                  value={draft.genero}
                  onChange={(e) => {
                    updateField('genero', e.target.value)
                    commit({genero: e.target.value || null})
                  }}
                  disabled={godReadOnly}
                  className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
                  <option value="">{t('ficha.generoOptions.unspecified')}</option>
                  {GENERO.map((g) => (
                    <option key={g} value={g}>
                      {t(`ficha.generoOptions.${g}`)}
                    </option>
                  ))}
                </select>
              </div>
              <DateField
                id="ficha-fecha-nacimiento"
                label={t('ficha.fechaNacimiento')}
                value={draft.fecha_nacimiento}
                max={maxAdultBirthDate()}
                hint={t('ficha.fechaNacimientoHelp')}
                disabled={godReadOnly}
                onChange={(v) => {
                  updateField('fecha_nacimiento', v)
                  commit({fecha_nacimiento: v || null})
                }}
              />
            </FieldGroup>
          )}

          {showPage(4) && (
            <FieldGroup>
              <CatalogOtherField
                id="ficha-eps"
                label={t('ficha.eps')}
                options={EPS_OPTIONS}
                value={draft.eps}
                onChange={(v) => updateField('eps', v)}
                onCommit={(v) => commit({eps: v || null})}
                disabled={godReadOnly}
              />
              <CatalogOtherField
                id="ficha-afp"
                label={t('ficha.afp')}
                options={AFP_OPTIONS}
                value={draft.afp}
                onChange={(v) => updateField('afp', v)}
                onCommit={(v) => commit({afp: v || null})}
                disabled={godReadOnly}
              />
              {/* Solo el contratista por prestación de servicios se afilia a su propia ARL; ocultar no borra el valor. */}
              {draft.tipo_vinculacion === 'prestacion_servicios' && (
                <div data-testid="ficha-arl-field">
                  <CatalogOtherField
                    id="ficha-arl"
                    label={t('ficha.arl')}
                    options={ARL_OPTIONS}
                    value={draft.arl}
                    onChange={(v) => updateField('arl', v)}
                    onCommit={(v) => commit({arl: v || null})}
                    hint={t('ficha.arlHelp')}
                    disabled={godReadOnly}
                  />
                </div>
              )}
            </FieldGroup>
          )}
        </div>

        {view === 'wizard' && page === TOTAL_PAGES && (
          <p className="mt-4 text-xs text-muted-foreground" data-testid="ficha-finish-hint">
            {t('ficha.finishHint')}
          </p>
        )}

        {view === 'wizard' && completed && page <= TOTAL_PAGES && (
          <p className="mt-4 text-sm text-primary" data-testid="ficha-complete-notice">
            {t('ficha.completeNotice')}
          </p>
        )}

        {view === 'wizard' && page <= TOTAL_PAGES && (
          <div className="surface-divider -mx-5 -mb-5 mt-5 flex items-center justify-between gap-3 border-t px-5 py-3.5">
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" disabled={page <= 2} onClick={() => setPage(page - 1)}>
                {t('common.back')}
              </Button>
              <Hint testId="ficha-autosave-hint">{t('ficha.autosaveHint')}</Hint>
            </div>
            {page < TOTAL_PAGES ? (
              <Button type="button" size="sm" onClick={() => goToPage(page + 1)}>
                {t('ficha.next')}
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  goToPage(TOTAL_PAGES)
                  setPage(RIESGOS_STEP)
                }}
                data-testid="ficha-finish">
                {t('ficha.finish')}
              </Button>
            )}
          </div>
        )}

        {editing && (
          <div className="surface-divider -mx-5 -mb-5 mt-5 flex items-center justify-between gap-3 border-t px-5 py-3.5">
            <Hint testId="ficha-autosave-hint">{t('ficha.autosaveHint')}</Hint>
            <Button type="button" size="sm" onClick={() => setView('resumen')} data-testid="mi-ficha-editar-listo">
              {t('ficha.editar.listo')}
            </Button>
          </div>
        )}
      </SectionCard>
    </div>
  )
}

/** Destino del enlace de registro: solo acá el asistente encadena ficha → entrevista → perfil. */
export function MiFichaPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {data: business} = useBusiness(businessId)

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeading title={t('ficha.title')} subtitle={business?.name} />
      <MiFichaBody businessId={businessId!} conPerfil />
    </div>
  )
}
